import React from "react";

const INLINE = { display: "inline-block", verticalAlign: "-0.125em" };


/**
 * Shared inline SVG icons for the games UI — the no-emoji replacements for
 * pictographic glyphs in dedicated icon slots (speakers, stars, meters,
 * result bursts). Copy text never embeds these; icons live only in icon
 * slots. All take `size` (px) and default to currentColor so the parent's
 * `color` styles them, unless a fill is intrinsic (star gold, flame orange).
 */

export const SpeakerIcon = ({ size = 18, muted = false, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M4 9.5v5h3.6L13 19V5L7.6 9.5H4z"
      fill={color}
      stroke={color}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    {muted ? (
      <path d="m16.2 9.2 5 5m0-5-5 5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    ) : (
      <>
        <path d="M16 9.4a4 4 0 0 1 0 5.2" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M18.4 7.2a7 7 0 0 1 0 9.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      </>
    )}
  </svg>
);

export const StarIcon = ({ size = 18, color = "#F2B400" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M12 2.6 14.9 8.5l6.5.9-4.7 4.6 1.1 6.5L12 17.4l-5.8 3.1 1.1-6.5L2.6 9.4l6.5-.9L12 2.6z"
      fill={color}
    />
  </svg>
);

export const HeartIcon = ({ size = 18, color = "#ED4C5C", outline = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M12 20.5S3.5 15.2 3.5 9.6a4.6 4.6 0 0 1 8.5-2.5 4.6 4.6 0 0 1 8.5 2.5c0 5.6-8.5 10.9-8.5 10.9z"
      fill={outline ? "none" : color}
      stroke={color}
      strokeWidth={outline ? 2 : 0}
    />
  </svg>
);

export const FlameIcon = ({ size = 18, color = "#F0701A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M12 2.5c.6 3-0.6 4.6-2 6-1.6 1.7-3.5 3.5-3.5 6.6a5.5 5.5 0 0 0 11 0c0-1.6-.5-2.9-1.3-4.1-.3 1-.9 1.8-1.9 2.3.6-2.6-.3-6.3-2.3-8.3z"
      fill={color}
    />
    <path
      d="M12 21a3.6 3.6 0 0 1-3.6-3.7c0-1.7 1-2.8 2-3.9.6-.7 1.2-1.3 1.6-2.1 1.4 1.6 3.6 3.7 3.6 6A3.6 3.6 0 0 1 12 21z"
      fill="#FFC24B"
    />
  </svg>
);

export const BoltIcon = ({ size = 18, color = "#F2B400" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path d="M13.5 2 5 13.4h5L10.5 22 19 10.6h-5L13.5 2z" fill={color} />
  </svg>
);

export const TrophyIcon = ({ size = 18, color = "#F2B400" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M7 3h10v2h4v2.5A4.5 4.5 0 0 1 16.6 12 5 5 0 0 1 13 14.9V17h3v2.5H8V17h3v-2.1A5 5 0 0 1 7.4 12 4.5 4.5 0 0 1 3 7.5V5h4V3zM5 7v.5A2.5 2.5 0 0 0 7 9.95V7H5zm14 0h-2v2.95A2.5 2.5 0 0 0 19 7.5V7z"
      fill={color}
    />
  </svg>
);

export const CrownIcon = ({ size = 18, color = "#F2B400" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M3 8.5 7.5 12 12 5l4.5 7L21 8.5 19.4 18H4.6L3 8.5z"
      fill={color}
      stroke={color}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

export const WrenchIcon = ({ size = 18, color = "#8896AC" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M20.5 6.2a5 5 0 0 1-6.6 6.2L7 19.3a2.1 2.1 0 0 1-3-3l6.9-6.9a5 5 0 0 1 6.2-6.6l-3 3 .7 2.4 2.4.7 3.3-2.7z"
      fill={color}
    />
  </svg>
);

export const MoonIcon = ({ size = 18, color = "#7A6FD0" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z"
      fill={color}
    />
  </svg>
);

export const HandIcon = ({ size = 18, color = "#F2A65E" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M8.5 11V4.8a1.3 1.3 0 0 1 2.6 0V10m0-5.6V3.4a1.3 1.3 0 0 1 2.6 0V10m0-5.4a1.3 1.3 0 0 1 2.6 0V11m0-4.4a1.3 1.3 0 0 1 2.6 0v7.9a6.5 6.5 0 0 1-6.5 6.5h-.9a6.5 6.5 0 0 1-5.4-2.9L4 14.6a1.4 1.4 0 0 1 2.3-1.6l2.2 2.5"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const HourglassIcon = ({ size = 18, color = "#8896AC" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M6 2h12v2.2c0 2.8-2 4.6-4 5.8 2 1.2 4 3 4 5.8V18v4H6v-4-2.2c0-2.8 2-4.6 4-5.8-2-1.2-4-3-4-5.8V2z"
      fill="none"
      stroke={color}
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
    <path d="M9 19h6l-3-3.4L9 19z" fill={color} />
  </svg>
);

export const MagnifierIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <circle cx="10.5" cy="10.5" r="6" fill="none" stroke={color} strokeWidth="2.2" />
    <path d="m15.2 15.2 5.3 5.3" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

export const RainbowIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false" style={INLINE}>
    <path d="M3 18a9 9 0 0 1 18 0" stroke="#ED4C5C" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M6.2 18a5.8 5.8 0 0 1 11.6 0" stroke="#F2B400" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M9.4 18a2.6 2.6 0 0 1 5.2 0" stroke="#38BD87" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

export const PlateIcon = ({ size = 18, color = "#F0701A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <circle cx="12" cy="12" r="8.5" fill="none" stroke={color} strokeWidth="1.9" />
    <circle cx="12" cy="12" r="4.5" fill="none" stroke={color} strokeWidth="1.7" />
  </svg>
);

export const SproutIcon = ({ size = 18, color = "#36A54B" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path d="M12 21v-8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path
      d="M12 13c0-3.5-2.6-6-6.5-6C5.5 10.6 8 13 12 13zm0-2c.4-3.2 2.8-5 6.5-4.8C18.3 9.5 15.8 11.4 12 11z"
      fill={color}
    />
  </svg>
);

export const PawIcon = ({ size = 18, color = "#2E9E77" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <ellipse cx="7" cy="8.6" rx="2" ry="2.7" fill={color} />
    <ellipse cx="12" cy="6.8" rx="2.1" ry="2.8" fill={color} />
    <ellipse cx="17" cy="8.6" rx="2" ry="2.7" fill={color} />
    <path d="M12 11.4c3.3 0 6 2.4 6 5.2 0 1.7-1.3 3-3 3-1.2 0-2.1-.6-3-.6s-1.8.6-3 .6c-1.7 0-3-1.3-3-3 0-2.8 2.7-5.2 6-5.2z" fill={color} />
  </svg>
);

export const SparkleIcon = ({ size = 18, color = "#F2B400" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={INLINE}>
    <path
      d="M12 2.5 13.8 9l6.5 1.8-6.5 1.8L12 19l-1.8-6.4L3.7 10.8 10.2 9 12 2.5z"
      fill={color}
    />
    <circle cx="19" cy="4.6" r="1.4" fill={color} />
  </svg>
);
